/**
 * The pack: items as affordances, and a grouped list that expands on a tap — the two
 * checkboxes that had to land together.
 *
 * These were held back as a pair, and for a reason: one wants a tap on an item to *write an
 * action*, the other wants a tap on an item to *expand a description*, and those are the same
 * tap. Here is the resolution, which is to stop them being the same tap without adding a
 * second control to the row:
 *
 * **Tapping the row expands it. The expansion contains the description *and* the action.**
 *
 * The action is a button whose label is the exact sentence it will write — *"ready the service
 * revolver"* — so nothing about it is a mystery click, and reading an item and acting on it
 * are one gesture apart rather than in competition for the same one. The cost is a tap: the old
 * ambition was one. What the tap buys is that the sentence is visible before it is chosen,
 * which matters precisely because these are *editable openings* rather than commands
 * (`turn/draft.ts`), and that the row needs no cryptic glyph button beside it — a 24-pixel
 * chevron per row is exactly the mobile target the mobile pass would have to come back and fix.
 *
 * **`<details>` for the same three reasons `Section.tsx` gives**, plus the one that killed
 * the hover version outright: description tooltips positioned with hand-computed coordinates on
 * `mouseenter`, and there is no `mouseenter` on a phone. The description is not hidden behind
 * a pointer here, it is behind a disclosure that a keyboard, a screen reader and Ctrl-F can
 * all reach.
 *
 * Ammo and uses are drawn as pips rather than written as `3 uses` in a metadata string —
 * counts, not fractions, because no capacity exists on the wire (`items.ts`).
 */
import type { InventoryItem } from '@/api/generated';
import { itemAction } from '@/features/character/actions';
import { groupInventory, MAX_PIPS, metersFor, type Meter } from '@/features/character/items';
import { useT } from '@/i18n/context';
import { Nothing } from '@/ui/Section';

interface InventoryProps {
  items: readonly InventoryItem[];
  /** Writes an editable opening into the action box and closes the panel over it. */
  onSuggest: (action: string) => void;
}

export function Inventory({ items, onSuggest }: InventoryProps) {
  const t = useT();

  if (items.length === 0) return <Nothing>{t('ui.panel.carrying_nothing')}</Nothing>;

  return (
    <div className="flex flex-col gap-4">
      {groupInventory(items).map((group) => (
        <section key={group.category}>
          <h4
            className="text-fg-faint mb-1 font-mono tracking-wider"
            style={{ fontSize: 'var(--text-meta)' }}
          >
            {group.label}
          </h4>
          <ul className="flex flex-col">
            {group.items.map((entry) => (
              <li key={entry.item}>
                <ItemRow entry={entry} onSuggest={onSuggest} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function ItemRow({
  entry,
  onSuggest,
}: {
  entry: InventoryItem;
  onSuggest: (action: string) => void;
}) {
  const t = useT();
  const action = itemAction(t, entry);
  const meters = metersFor(t, entry);

  return (
    <details>
      <summary
        className="text-fg-muted hover:text-fg flex cursor-pointer list-none items-baseline gap-2 py-1 font-mono transition-colors"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        <span className="truncate">{entry.display_name}</span>
        {/* A stack is a quantity, not a meter: seven tokens are not seven out of anything. */}
        {entry.quantity > 1 ? (
          <span className="text-fg-faint tabular-nums">×{entry.quantity}</span>
        ) : null}
        <span className="ml-auto flex shrink-0 items-baseline gap-2">
          {meters.map((meter) => (
            <Pips key={meter.kind} meter={meter} />
          ))}
        </span>
      </summary>

      <div className="border-rule mt-1 mb-2 ml-1 flex flex-col items-start gap-2 border-l pl-3">
        {entry.description.length > 0 ? (
          <p className="text-fg-faint" style={{ fontSize: 'var(--text-meta)' }}>
            {entry.description}
          </p>
        ) : null}

        <button
          type="button"
          onClick={() => {
            onSuggest(action);
          }}
          className="text-fg-muted hover:text-accent flex gap-2 text-left font-mono transition-colors"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {/* The same `›` the feed marks the player's own words with (`Scene.tsx`): this is a
              sentence that is about to become one of them. */}
          <span aria-hidden="true" className="text-fg-faint select-none">
            ›
          </span>
          <span>{action}</span>
        </button>
      </div>
    </details>
  );
}

/**
 * The count as dots. Past `MAX_PIPS` the dots have stopped being countable and the number is
 * the better rendering — a magazine of thirty is a number, not a pattern.
 *
 * The dots are `aria-hidden` and the row carries `meter.label`, so a screen reader hears
 * "4 rounds" rather than a run of bullet characters.
 */
function Pips({ meter }: { meter: Meter }) {
  if (meter.count > MAX_PIPS || meter.count === 0) {
    return (
      <span className="text-fg-faint tabular-nums" style={{ fontSize: 'var(--text-meta)' }}>
        {meter.label}
      </span>
    );
  }

  return (
    <span className="flex items-center gap-0.5" title={meter.label}>
      <span className="sr-only">{meter.label}</span>
      {Array.from({ length: meter.count }, (_, index) => (
        <span
          key={index}
          aria-hidden="true"
          className={`size-1 rounded-full ${meter.kind === 'ammo' ? 'bg-fg-muted' : 'bg-accent'}`}
        />
      ))}
    </span>
  );
}
